import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import UniversalLookup from './UniversalLookup';
import { usePermissions } from './usePermissions';
import { useFontSize } from './FontSizeContext';
import { useTranslation } from 'react-i18next';
import { exportToExcel } from './exportUtils';
import {
  Search, RefreshCw, Globe, Building2, MapPin, X, CheckSquare, Trash2, Printer, RotateCcw, Star
} from 'lucide-react';
import toast from 'react-hot-toast';
import Swal from 'sweetalert2';
import StatusBadge from './StatusBadge';
import ListPageShell from './list/ListPageShell';
import SortableTh from './list/SortableTh';
import { swalTheme, swalDangerColor } from '../lib/swalTheme';
import AssetRecycleBin from './AssetRecycleBin';
import { useFavoriteFilters } from './useFavoriteFilters';

const AssetsList = () => {
  const { t, i18n } = useTranslation();
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  const session = JSON.parse(localStorage.getItem('user') || '{}');
  const sessionSite = session.site_id || '';
  const sessionOrg = session.org_id || '';
  const isSuperAdmin = session.role?.name === 'SUPER_ADMIN';
  const isRTL = i18n.language === 'ar' || i18n.language === 'ur';

  // 1. State الفلاتر (كاملة كما هي في ملفك الأصلي)
  const [filters, setFilters] = useState({
    assetnum: "",
    description: "",
    status: "",
    location_id: "",
    site_id: "",
    org_id: ""
  });

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedOrg, setSelectedOrg] = useState("");
  const [selectedSite, setSelectedSite] = useState("");
  const [orgsList, setOrgsList] = useState([]);
  const [sitesList, setSitesList] = useState([]);
  const [lookup, setLookup] = useState({ isOpen: false, title: '', type: '', domainId: '', field: '', filters: {}, onCustomSelect: null });
  const [fieldMaps, setFieldMaps] = useState({});
  const [showRecycleBin, setShowRecycleBin] = useState(false);
  
  // 2. إعدادات الترتيب والباجينج (Server-side Integration)
  const [sortConfig, setSortConfig] = useState({ key: 'assetnum', direction: 'asc' });
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0); 
  const itemsPerPage = 20;
  
  const [selectedAssetIds, setSelectedAssetIds] = useState([]); 
  
  const [isBulkMoveOpen, setIsBulkMoveOpen] = useState(false); 
  const { canCreate, canUpdate, canDelete } = usePermissions('assets');
  const [showAllSites, setShowAllSites] = useState(false);

  const { saveFavorite } = useFavoriteFilters('ASSET');
  const handleSaveFavoriteFilter = async () => {
    const { value: name } = await Swal.fire({
      ...swalTheme(),
      title: t('favorites.namePrompt', 'Name this favorite filter'),
      input: 'text',
      inputPlaceholder: t('favorites.namePlaceholder', 'e.g. My Open WOs'),
      showCancelButton: true,
      confirmButtonText: t('common.save', 'Save'),
      cancelButtonText: t('common.cancel', 'Cancel'),
      inputValidator: (value) => !value?.trim() && t('favorites.nameRequired', 'Please enter a name')
    });
    if (name && name.trim()) {
      saveFavorite(name, { ...filters, searchTerm, all_sites: showAllSites, selectedOrg, selectedSite });
    }
  };

  // 🎯 تطبيق فلتر مفضّل - جاي من زرار السايدبار (AppSidebar.js) عن طريق
  // حدث عام، نفس أسلوب أمر الشغل بالظبط
  useEffect(() => {
    const applyFavorite = (e) => {
      if (e.detail?.appName === 'ASSET' && e.detail?.filters) {
        const { searchTerm: favSearchTerm, all_sites: favAllSites, selectedOrg: favOrg, selectedSite: favSite, ...favFilters } = e.detail.filters;
        setFilters(prev => ({ ...prev, ...favFilters }));
        if (favSearchTerm !== undefined) setSearchTerm(favSearchTerm);
        if (favAllSites !== undefined) setShowAllSites(favAllSites);
        if (favOrg !== undefined) setSelectedOrg(favOrg);
        if (favSite !== undefined) setSelectedSite(favSite);
        setCurrentPage(1);
      }
    };
    window.addEventListener('APPLY_FAVORITE_FILTER', applyFavorite);
    return () => window.removeEventListener('APPLY_FAVORITE_FILTER', applyFavorite);
  }, []);

  const fontContext = useFontSize();
  const fontSize = fontContext?.fontSize || 13;
  const searchParams = new URLSearchParams(location.search);
  const [page, setPage] = useState(parseInt(searchParams.get('page')) || 1);
  const [activeQuery, setActiveQuery] = useState('all'); // 'all', 'operating', 'critical'
  const [isBulkStatusOpen, setIsBulkStatusOpen] = useState(false);

  // 🎯 دالة جلب البيانات: تجمع بين كل الفلاتر والباجينج وترسلها للسيرفر
  const fetchAssets = useCallback(async (isAll = showAllSites) => {
    setLoading(true);
    const token = localStorage.getItem('token');
    const headers = { 'Authorization': `Bearer ${token}` };
    
    try {
        const forceAllSites = isSuperAdmin && (selectedOrg !== "" || selectedSite !== "");
        const effectiveAllSites = isAll || forceAllSites;

        const params = {
            page: currentPage,
            limit: itemsPerPage,
            q: searchTerm,
            query_type: activeQuery,
            all_sites: isSuperAdmin ? Boolean(effectiveAllSites) : false,
            assetnum: filters.assetnum,
            description: filters.description,
            status: filters.status,
            location_id: filters.location_id,
            org_id: effectiveAllSites ? (selectedOrg || filters.org_id) : sessionOrg,
            site_id: effectiveAllSites ? (selectedSite || filters.site_id) : sessionSite,
            session_org: sessionOrg,
            session_site: sessionSite
        };

        Object.keys(params).forEach(key => (params[key] === "" || params[key] === undefined) && delete params[key]);

        const res = await axios.get('/api/assets', { headers, params });
        setAssets(res.data.data || []);
        setTotalItems(res.data.total || 0);
    } catch (err) {
        console.error("Fetch Error:", err);
        toast.error(t('scripts.toasts.fetch_fail'));
    } finally {
        setLoading(false);
    }
  }, [currentPage, searchTerm, filters, selectedOrg, selectedSite, showAllSites, sessionSite, sessionOrg, isSuperAdmin, activeQuery, t]);

  useEffect(() => {
    fetchAssets();
  }, [fetchAssets]);

  const selectedAssetIdsRef = useRef(selectedAssetIds);
  useEffect(() => {
    selectedAssetIdsRef.current = selectedAssetIds;
  }, [selectedAssetIds]);

  const fetchInitialData = useCallback(async () => {
    const token = localStorage.getItem('token');
    const headers = { 'Authorization': `Bearer ${token}` };
    try {
      const orgsRes = await axios.get('/api/organizations', { headers });
      setOrgsList(orgsRes.data);
    } catch (err) { console.error("Initial Load Error:", err); }
  }, []);
  
  useEffect(() => { fetchInitialData(); }, [fetchInitialData]);

  const handleSmartSearch = (fieldName, title, defaultType, onCustomSelect = null) => {
    let extraFilters = {};
    if (fieldName === 'itemnum' && defaultType === 'spareparts') {
      extraFilters = { sparepart: true };
    }
    if (fieldName === "targetLoc"){
      extraFilters = {org_id: sessionOrg, site_id: sessionSite}
    }
    const linkedDomain = (fieldMaps || {})[fieldName];
    setLookup({ 
      isOpen: true, 
      title, 
      type: linkedDomain ? 'DOMAIN' : defaultType, 
      domainId: linkedDomain || '', 
      field: fieldName,
      filters: extraFilters,
      onCustomSelect: onCustomSelect
    });
  };

  const handleExport = useCallback(() => {
    // 🎯 الحل السحري: نستخدم الـ State الداخلي بدل الرابط لأن القيم الحقيقية متخزنة هنا
    const currentSiteFilter = filters.site_id;
    const effectiveSite = !showAllSites && !currentSiteFilter ? sessionSite : currentSiteFilter;

    // تجميع كل الفلاتر النشطة (الحقول + البحث السريع + فيو السايدبار)
    let activeFilters = {
        ...filters,
        searchTerm: searchTerm,
        //org_id: sessionOrg,
    };

    if (effectiveSite) {
        activeFilters.site_id = effectiveSite;
    } else {
        delete activeFilters.site_id; 
    }

    // تنظيف الفلاتر الفاضية عشان متبعتش للباك إند
    activeFilters = Object.fromEntries(
        Object.entries(activeFilters).filter(([_, value]) => value !== '' && value !== null)
    );

    const columnsMap = {
        "assetnum": t('wo.fields.asset', 'Asset'),
        "description": t('wo.fields.description', 'Description'),
        "status": t('wo.headers.status', 'Status'),
        "location_id": t('reports.designer.fields.location', 'Location'),
        "site_id": t('wo.fields.site', 'Site'),
        "org_id": t('assets.list.col_org', 'Organization')
    };

    exportToExcel('assets', activeFilters, columnsMap, t);
  }, [filters, searchTerm, showAllSites, sessionSite, sessionOrg, t]);

  useEffect(() => {
    const exportData = () => { handleExport(); };
    window.addEventListener('GLOBAL_EXPORT', exportData);
    return () => window.removeEventListener('GLOBAL_EXPORT', exportData);
  }, [handleExport]);

  useEffect(() => {
    if (selectedOrg) {
      const org = orgsList.find(o => o.org_id === selectedOrg);
      setSitesList(org ? org.sites : []);
    } else {
      setSitesList([]);
    }
    setSelectedSite(""); 
    setCurrentPage(1); 
  }, [selectedOrg, orgsList]);

  const toggleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedAssetIds(assets.map(a => a.asset_id));
    } else {
      setSelectedAssetIds([]);
    }
  };

  const toggleSelectAsset = (assetId) => {
    setSelectedAssetIds(prev => 
      prev.includes(assetId) 
        ? prev.filter(id => id !== assetId) 
        : [...prev, assetId]
    );
  };

  // 🎯 دالة الحذف الجماعي المباشرة
  // 🎯 دالة الحذف الجماعي المباشرة (استخدمنا الـ Ref عشان نقرأ الداتا الحية)
  const handleBulkDeleteClick = useCallback(() => {
    const idsToDelete = selectedAssetIdsRef.current; // 👈 السر هنا
    
    if (idsToDelete.length === 0) return;

    Swal.fire({
      ...swalTheme(),
      title: t('common.areYouSure', 'Are you sure?'),
      text: t('assets.list.deleteConfirmText', `You are about to delete ${idsToDelete.length} assets. They'll be moved to the Recycle Bin and can be restored later.`),
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: swalDangerColor(),
      confirmButtonText: t('common.yesDelete', 'Yes, delete them!'),
      cancelButtonText: t('common.cancel', 'Cancel'),
      reverseButtons: isRTL
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const token = localStorage.getItem('token');
          const headers = { 'Authorization': `Bearer ${token}` };

          await axios.delete('/api/assets/bulk', {
            headers,
            data: { ids: idsToDelete } // 👈 وهنا كمان
          });

          Swal.fire({
            ...swalTheme(),
            icon: 'success',
            title: t('common.deleted', 'Deleted!'),
            text: t('assets.list.deleteSuccess', 'Assets have been deleted successfully.'),
            confirmButtonText: t('common.ok', 'OK')
          });

          setSelectedAssetIds([]); // تفريغ التحديد
          fetchAssets();   // تحديث الجدول
        } catch (err) {
          console.error("Bulk Delete Error:", err);
          Swal.fire({
            ...swalTheme(),
            icon: 'error',
            title: t('common.error', 'Error!'),
            text: err.response?.data?.detail || t('assets.list.deleteError', 'Failed to delete. Some assets might be linked to work orders or meters.'),
            confirmButtonText: t('common.ok', 'OK')
          });
        }
      }
    });
  }, [t, isRTL, fetchAssets]);

  // 🎯 عشان لو اليوزر ضغط من السايدبار برضه تشتغل
  useEffect(() => {
    const handleSidebarDelete = () => {
      if (selectedAssetIdsRef.current.length > 0) {
        handleBulkDeleteClick();
      } else {
        Swal.fire({
          ...swalTheme(),
          icon: 'info',
          title: t('common.alert', 'Alert'),
          text: t('assets.list.noSelectionDelete', 'Please select at least one asset to delete.'),
          confirmButtonText: t('common.ok', 'OK')
        });
      }
    };
    window.addEventListener('GLOBAL_BULK_DELETE_HANDLER', handleSidebarDelete);
    return () => window.removeEventListener('GLOBAL_BULK_DELETE_HANDLER', handleSidebarDelete);
  }, [t, handleBulkDeleteClick]); // 👈 ضفنا الدالة هنا

  // 🎯 دالة طباعة الباركود أو الـ QR Code (ISO Standard - Blob Handling)
  // codeType: 'barcode' | 'qrcode' - بيتحدد من مودال الاختيار تحت
  const handleBulkPrintBarcodes = useCallback(async (codeType = 'barcode') => {
    const idsToPrint = selectedAssetIdsRef.current;

    if (idsToPrint.length === 0) return;

    // توست تحميل عشان لو التقرير بياخد وقت
    const toastId = toast.loading(isRTL ? 'جاري تجهيز التقرير...' : 'Generating Report...');

    try {
      const token = localStorage.getItem('token');

      // نستخدم POST عشان نقدر نبعت مئات الأصول بدون مشكلة URL Length
      // responseType: 'blob' هي الزتونة عشان نتعامل مع الملفات (PDF)
      const response = await axios.post('/api/reports/assets/barcodes', {
        asset_ids: idsToPrint,
        code_type: codeType
      }, {
        headers: { 'Authorization': `Bearer ${token}` },
        responseType: 'blob'
      });

      // تحويل الـ Blob لـ URL وهمي داخل المتصفح
      const fileURL = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));

      toast.success(isRTL ? 'تم تجهيز التقرير!' : 'Report generated!', { id: toastId });

      // فتح التقرير في تابة جديدة جاهزة للطباعة
      const printWindow = window.open(fileURL, '_blank');
      if (printWindow) {
        printWindow.focus();
      } else {
        // لو المتصفح مانع الـ Pop-ups
        toast.error(isRTL ? 'يرجى السماح بالنوافذ المنبثقة (Pop-ups).' : 'Please allow pop-ups.');
      }

    } catch (err) {
      console.error("Print Barcodes Error:", err);
      toast.error(isRTL ? 'فشل تجهيز التقرير.' : 'Failed to generate report.', { id: toastId });
    }
  }, [isRTL]);

  // 🎯 قبل الطباعة، لازم اليوزر يختار باركود ولا QR Code - نفس القرار بيتحدد
  // في الطلب المبعوت للباك إند (code_type) عشان يرن الشكل الصح في التقرير
  const promptPrintCodeType = useCallback(async () => {
    if (selectedAssetIdsRef.current.length === 0) {
      Swal.fire({
        ...swalTheme(),
        icon: 'info',
        title: t('common.alert', 'Alert'),
        text: t('assets.list.noSelectionPrint', 'Please select at least one asset to print.'),
        confirmButtonText: t('common.ok', 'OK')
      });
      return;
    }

    const result = await Swal.fire({
      ...swalTheme(),
      icon: 'question',
      title: t('assets.list.printChoiceTitle', 'Choose Print Format'),
      text: t('assets.list.printChoiceText', 'Do you want to print Barcode or QR Code for the selected assets?'),
      showDenyButton: true,
      showCancelButton: true,
      confirmButtonText: t('assets.list.printBarcodeOption', 'Barcode'),
      denyButtonText: t('assets.list.printQrOption', 'QR Code'),
      cancelButtonText: t('common.cancel', 'Cancel'),
    });

    if (result.isConfirmed) handleBulkPrintBarcodes('barcode');
    else if (result.isDenied) handleBulkPrintBarcodes('qrcode');
  }, [t, handleBulkPrintBarcodes]);

  // 🎯 استقبال الإشارة من السايدبار
  useEffect(() => {
    window.addEventListener('GLOBAL_BULK_PRINT_BARCODES_HANDLER', promptPrintCodeType);
    return () => window.removeEventListener('GLOBAL_BULK_PRINT_BARCODES_HANDLER', promptPrintCodeType);
  }, [promptPrintCodeType]);


  const BulkStatusModal = ({ isOpen, onClose, selectedIds, onRefresh, t, isRTL }) => {
  const [newStatus, setNewStatus] = useState("");
  const [memo, setMemo] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);

  const handleBulkSubmit = async () => {
    setIsProcessing(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { 'Authorization': `Bearer ${token}` };
      
      await axios.post('/api/assets/bulk-status', {
        ids: selectedIds,
        new_status: newStatus,
        memo: memo
      }, { headers });
      
      toast.success(isRTL ? 'تم تحديث الحالة بنجاح' : 'Status updated successfully');
      onRefresh();
      onClose();
    } catch (err) {
      toast.error(isRTL ? 'فشل تحديث الحالة' : 'Failed to update status');
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-md p-4" dir={isRTL ? 'rtl' : 'ltr'}>
      <div className={`bg-white w-full max-w-[550px] shadow-2xl border-t-8 border-[#1a3353] rounded-sm overflow-hidden animate-in zoom-in-95 duration-200 ${isRTL ? 'font-cairo' : ''}`}>
        <div className={`p-5 border-b flex justify-between items-center bg-slate-50 `}>
          <h2 className="font-black text-[#1a3353] uppercase tracking-widest text-sm flex items-center gap-3">
            <RefreshCw size={22} className="text-blue-500" /> {t('common.change_status', 'CHANGE STATUS')}
          </h2>
          <X size={20} className="cursor-pointer text-slate-400 hover:text-red-500 transition-colors" onClick={onClose} />
        </div>
        
        <div className="p-8 space-y-6">
          <div className="bg-blue-50 border-l-4 border-blue-400 p-4">
            <p className={`text-[11px] font-black text-blue-800 uppercase leading-relaxed ${isRTL ? 'text-right' : ''}`}>
              {isRTL ? 'سيتم تغيير حالة ' : 'You are changing the status of '} 
              <span className="underline">{selectedIds.length} {t('assets.modal.caution_suffix')}</span>
            </p>
          </div>

          <div className="space-y-4 text-left">
            <div>
              <label className={`text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1 ${isRTL ? 'text-right block' : ''}`}>{t('wo.headers.status', 'NEW STATUS')}</label>
              <select 
                className="w-full border-b-2 border-slate-200 p-3 text-lg font-black text-blue-900 outline-none focus:border-blue-600 transition-all uppercase"
                value={newStatus}
                onChange={(e) => setNewStatus(e.target.value)}
              >
                <option value="">{t('common.select', '-- SELECT STATUS --')}</option>
                <option value="OPERATING">OPERATING</option>
                <option value="NOT OPERATING">NOT OPERATING</option>
                <option value="DECOMMISSIONED">DECOMMISSIONED</option>
                <option value="INACTIVE">INACTIVE</option>
                <option value="NOT READY">NOT READY</option>
              </select>
            </div>
            
            <div>
              <label className={`text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1 ${isRTL ? 'text-right block' : ''}`}>{t('common.memo', 'MEMO (OPTIONAL)')}</label>
              <input 
                className="w-full border-b-2 border-slate-200 p-3 text-sm font-bold text-gray-700 outline-none focus:border-blue-600 transition-all"
                placeholder="..."
                value={memo}
                onChange={(e) => setMemo(e.target.value)}
              />
            </div>
          </div>
        </div>

        <div className={`p-4 bg-slate-50 border-t flex justify-end gap-3 px-8`}>
          <button onClick={onClose} className="px-6 py-2 font-black uppercase text-slate-400 text-[11px] hover:text-slate-600 transition-colors">{t('assets.modal.abort')}</button>
          <button 
            disabled={!newStatus || isProcessing}
            onClick={handleBulkSubmit}
            className="bg-[#005495] text-white px-10 py-2.5 rounded-sm font-black uppercase text-[11px] shadow-xl hover:bg-black disabled:opacity-30 transition-all flex items-center gap-2"
          >
            {isProcessing ? <RefreshCw className="animate-spin" size={14}/> : t('assets.modal.execute')}
          </button>
        </div>
      </div>
    </div>
  );
};

  // 🎯 عشان لو اليوزر ضغط من السايدبار برضه تشتغل
  useEffect(() => {
    const handleSidebarDelete = () => {
      if (selectedAssetIdsRef.current.length > 0) {
        handleBulkDeleteClick();
      } else {
        Swal.fire({
          ...swalTheme(),
          icon: 'info',
          title: t('common.alert', 'Alert'),
          text: t('assets.list.noSelectionDelete', 'Please select at least one asset to delete.'),
          confirmButtonText: t('common.ok', 'OK')
        });
      }
    };
    window.addEventListener('GLOBAL_BULK_DELETE_HANDLER', handleSidebarDelete);
    return () => window.removeEventListener('GLOBAL_BULK_DELETE_HANDLER', handleSidebarDelete);
  }, [t, isRTL, fetchAssets]); // ضفنا الـ dependencies عشان الدالة تكون دايماً up to date

  useEffect(() => {
    const openBulkStatusModal = () => {
      setIsBulkMoveOpen(true);
    };
    window.addEventListener('GLOBAL_BULK_MOVE_HANDLER', openBulkStatusModal);
    return () => window.removeEventListener('GLOBAL_BULK_MOVE_HANDLER', openBulkStatusModal);
  }, []);

  useEffect(() => {
    const openBulkStatusModal = () => {
      setIsBulkStatusOpen(true);
    };
    window.addEventListener('GLOBAL_BULK_STATUS_HANDLER', openBulkStatusModal);
    return () => window.removeEventListener('GLOBAL_BULK_STATUS_HANDLER', openBulkStatusModal);
  }, []);

  const sortedAssets = useMemo(() => {
    let sortableItems = [...assets];
    if (sortConfig.key) {
      sortableItems.sort((a, b) => {
        const aVal = a[sortConfig.key] || '';
        const bVal = b[sortConfig.key] || '';
        if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }
    return sortableItems;
  }, [assets, sortConfig]);

  const requestSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
    setCurrentPage(1);
  };

  const handleRowClick = (asset_id) => {
    const activeParams = {
        ...filters,
        searchTerm: searchTerm,
        page: currentPage,
        sortBy: sortConfig.key,
        sortDir: sortConfig.direction,
        all_sites: showAllSites,
        user_siteid: selectedSite || sessionSite, 
        orgid: selectedOrg || sessionOrg
    };
    const cleanParams = Object.fromEntries(
        Object.entries(activeParams).filter(([_, v]) => v !== '' && v !== null && v !== undefined)
    );
    const queryString = new URLSearchParams(cleanParams).toString();
    navigate(`/assets/${asset_id}${queryString ? '?' + queryString : ''}`);
  };

  useEffect(() => {
      const openAllSites = () => { 
          if (isSuperAdmin) {
              setShowAllSites(true); 
              setCurrentPage(1); 
          }
      };
      const viewMySite = () => { 
          setShowAllSites(false); 
          setCurrentPage(1); 
      };
      window.addEventListener('GLOBAL_VIEW_ALL_SITES', openAllSites);
      window.addEventListener('GLOBAL_VIEW_MY_SITE', viewMySite);
      return () => {
          window.removeEventListener('GLOBAL_VIEW_ALL_SITES', openAllSites);
          window.removeEventListener('GLOBAL_VIEW_MY_SITE', viewMySite);
      };
  }, [isSuperAdmin]);

  useEffect(() => {
      const openCritical = () => {setActiveQuery('critical'); setCurrentPage(1);};
      const openOperating = () => {setActiveQuery('operating'); setCurrentPage(1);};
      window.addEventListener('GLOBAL_VIEW_CRTITICAL', openCritical);
      window.addEventListener('GLOBAL_VIEW_OPERATING', openOperating);
      return () => {
            window.removeEventListener('GLOBAL_VIEW_CRTITICAL', openCritical);
            window.removeEventListener('GLOBAL_VIEW_OPERATING', openOperating);
      }
  },[]);

  const totalPages = Math.ceil(totalItems / itemsPerPage);

  const handleInputChange = (field, val) => {
    setFilters(prev => ({ ...prev, [field]: val }));
  };

  return (
    <div className="h-full" style={{ fontSize: `${fontSize}px` }}>
    <ListPageShell
      title={t('assets.list.system_title')}
      loading={loading}
      onRefresh={() => fetchAssets()}
      searchValue={searchTerm}
      onSearchChange={(v) => { setSearchTerm(v); setCurrentPage(1); }}
      searchPlaceholder={t('scripts.search_placeholder')}
      newButtonLabel={t('assets.list.new_asset')}
      onNew={() => navigate('/assets/new')}
      canCreate={canCreate}
      isRTL={isRTL}
      currentPage={currentPage}
      totalPages={totalPages}
      totalItems={totalItems}
      onPageChange={setCurrentPage}
      toolbarExtra={
        <div className={`flex items-center gap-3 ${isRTL ? 'mr-4' : 'ml-4'}`}>
          <button
            onClick={() => setShowRecycleBin(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-control border border-line bg-surface-panel text-ink-secondary text-[10px] font-bold uppercase hover:bg-primary-soft transition-all"
          >
            <RotateCcw size={13} /> {t('customApps.recycleBin', 'Recycle Bin')}
          </button>
          <button
            onClick={handleSaveFavoriteFilter}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-control border border-line bg-surface-panel text-status-warning text-[10px] font-bold uppercase hover:bg-primary-soft transition-all"
          >
            <Star size={13} /> {t('favorites.save', 'SAVE AS FAVORITE')}
          </button>
          {isSuperAdmin && (
            <div className="flex items-center gap-4 bg-surface-panel p-2.5 rounded-control border border-line w-fit shadow-sm">
              <div className={`flex items-center gap-2 border-line ${isRTL ? 'border-l pl-6' : 'border-r pr-6'}`}>
                <Globe size={16} className="text-primary" />
                <span className="text-ink-secondary italic">Org:</span>
                <select
                  className="bg-transparent outline-none font-black text-primary uppercase cursor-pointer"
                  value={selectedOrg}
                  onChange={(e) => setSelectedOrg(e.target.value)}
                >
                  <option value="">{t('assets.list.all_orgs')}</option>
                  {orgsList.map(org => (<option key={org.org_id} value={org.org_id}>{org.org_id}</option>))}
                </select>
              </div>

              <div className={`flex items-center gap-2 border-line ${isRTL ? 'border-l pl-6' : 'border-r pr-6'}`}>
                <Building2 size={16} className="text-primary" />
                <span className="text-ink-secondary italic">Site:</span>
                <select
                  className="bg-transparent outline-none font-black text-primary uppercase cursor-pointer disabled:opacity-30"
                  value={selectedSite}
                  onChange={(e) => setSelectedSite(e.target.value)}
                  disabled={!selectedOrg}
                >
                  <option value="">{t('assets.list.all_sites')}</option>
                  {sitesList.map(site => (<option key={site.site_id} value={site.site_id}>{site.site_id}</option>))}
                </select>
              </div>

              {(selectedOrg || searchTerm || filters.assetnum || filters.status) && (
                <button
                  onClick={() => {
                      setSelectedOrg(""); setSelectedSite(""); setSearchTerm("");
                      setFilters({assetnum: "", description: "", status: "", location_id: "", site_id: "", org_id: ""});
                      setCurrentPage(1);
                  }}
                  className="text-[10px] text-status-danger font-black uppercase hover:bg-status-danger-bg px-3 py-1 rounded transition-all flex items-center gap-1 border border-status-danger-bg"
                >
                  <X size={12}/> {t('common.cancel')}
                </button>
              )}
            </div>
          )}
        </div>
      }
      headerBanner={selectedAssetIds.length > 0 && (
        <div className="bg-[#fb8c00] text-white p-2.5 shadow-2xl flex justify-between items-center animate-in slide-in-from-top duration-300">
          <div className={`flex items-center gap-4 ${isRTL ? 'mr-10' : 'ml-10'}`}>
            <CheckSquare size={20} />
            <span className="font-black text-xs uppercase tracking-widest">
              {selectedAssetIds.length} {t('common.selected')}
            </span>
            <button
              onClick={() => setSelectedAssetIds([])}
              className="text-[10px] bg-black/20 hover:bg-black/40 px-3 py-1 rounded-full transition-all uppercase font-bold"
            >
              {t('assets.modal.abort')}
            </button>
          </div>
          <div className={`flex gap-2 ${isRTL ? 'ml-10' : 'mr-10'}`}>
            <button
              onClick={() => setIsBulkMoveOpen(true)}
              className="bg-white text-[#fb8c00] px-6 py-1.5 rounded-sm font-black text-[10px] uppercase shadow-md hover:bg-slate-100 transition-all flex items-center gap-2"
            >
              <MapPin size={14} /> {t('common.bulk_move')}
            </button>
            <button onClick={() => setIsBulkStatusOpen(true)} className="bg-primary-dark text-white px-6 py-1.5 rounded-sm font-black text-[10px] uppercase shadow-md hover:bg-black transition-all flex items-center gap-2">
              <RefreshCw size={14} /> {t('common.change_status')}
            </button>
            {canDelete && (
              <button
                onClick={handleBulkDeleteClick}
                className="bg-status-danger text-white px-6 py-1.5 rounded-sm font-black text-[10px] uppercase shadow-md hover:bg-red-700 transition-all flex items-center gap-2"
              >
                <Trash2 size={14} /> {t('sidebar.bulk_delete', 'DELETE SELECTED')}
              </button>
            )}
            <button
              onClick={promptPrintCodeType}
              className="bg-white text-slate-800 px-6 py-1.5 rounded-sm font-black text-[10px] uppercase shadow-md hover:bg-slate-200 transition-all flex items-center gap-2"
            >
              <Printer size={14} className="text-slate-600" /> {t('common.print_barcodes', 'PRINT BARCODES')}
            </button>
          </div>
        </div>
      )}
    >
      <table className="w-full text-left border-collapse">
        <thead className="bg-primary-dark text-white font-black uppercase sticky top-0 z-30 shadow-md">
          <tr className={isRTL ? 'flex-row-reverse' : ''}>
            <th className="p-3 border-r border-white/10 w-12 text-center">
              <input type="checkbox" className="w-4 h-4 cursor-pointer accent-orange-500" checked={assets.length > 0 && selectedAssetIds.length === assets.length} onChange={toggleSelectAll} />
            </th>
            {[
              { label: t('reports.designer.fields.assetnum'), key: "assetnum" },
              { label: t('reports.designer.fields.desc'), key: "description" },
              { label: t('reports.designer.fields.status'), key: "status" },
              { label: t('reports.designer.fields.location'), key: "location_id" },
              { label: t('assets.list.col_site'), key: "site_id" },
              { label: t('assets.list.col_org'), key: "org_id" }
            ].map(col => (
              <SortableTh key={col.key} label={col.label} sortKey={col.key} sortConfig={sortConfig} onSort={requestSort} isRTL={isRTL} />
            ))}
          </tr>
          <tr className="bg-surface-panel shadow-inner">
            <td></td>
            <td className="p-1.5 border-r border-line"><input name="assetnum" value={filters.assetnum} onChange={handleFilterChange} className="w-full border border-line p-1 outline-none font-bold text-primary uppercase" placeholder="..." /></td>
            <td className="p-1.5 border-r border-line"><input name="description" value={filters.description} onChange={handleFilterChange} className="w-full border border-line p-1 outline-none font-bold text-primary" placeholder="..." /></td>
            <td className="p-1.5 border-r border-line">
              <select name="status" value={filters.status} onChange={handleFilterChange} className="w-full border border-line p-1 outline-none font-bold text-primary uppercase">
                <option value="">{t('assets.list.all')}</option>
                <option value="OPERATING">OPERATING</option>
                <option value="NOT OPERATING">NOT OPERATING</option>
                <option value="DECOMMISSIONED">DECOMMISSIONED</option>
                <option value="INACTIVE">INACTIVE</option>
                <option value="NOT READY">NOT READY</option>
                <option value="MISSING">MISSING</option>
                <option value="SEALED">SEALED</option>
                <option value="BROKEN">BROKEN</option>
              </select>
            </td>
            <td className="p-1.5 border-r border-line"><input name="location_id" value={filters.location_id} onChange={handleFilterChange} className="w-full border border-line p-1 outline-none font-bold text-primary uppercase" placeholder="..." /></td>
            <td className="p-1.5 border-r border-line"><input name="site_id" value={filters.site_id} onChange={handleFilterChange} className="w-full border border-line p-1 outline-none font-bold text-primary uppercase" placeholder="..." /></td>
            <td className="p-1.5 border-r border-line"><input name="org_id" value={filters.org_id} onChange={handleFilterChange} className="w-full border border-line p-1 outline-none font-bold text-primary uppercase" placeholder="..." /></td>
          </tr>
        </thead>
        <tbody className="divide-y divide-line-subtle">
          {loading ? (
            <tr><td colSpan="7" className="p-20 text-center italic text-primary font-black animate-pulse text-sm uppercase tracking-widest bg-surface-panel">{t('workflow.loading')}</td></tr>
          ) : sortedAssets.length === 0 ? (
            <tr><td colSpan="7" className="p-20 text-center italic text-ink-secondary text-xs font-black uppercase bg-surface-panel border-dashed border-2 m-4">{t('dashboard.no_data')}</td></tr>
          ) : sortedAssets.map((asset, i) => (
            <tr key={i} className={`hover:bg-primary-soft cursor-pointer group transition-all ${isRTL ? 'text-right' : ''}`} onClick={() => handleRowClick(asset.asset_id)}>
              <td className="p-3.5 border-r border-line-subtle text-center" onClick={(e) => e.stopPropagation()}>
                <input type="checkbox" className="w-4 h-4 cursor-pointer accent-orange-600" checked={selectedAssetIds.includes(asset.asset_id)} onChange={() => toggleSelectAsset(asset.asset_id)} />
              </td>
              <td className="p-3.5 border-r border-line-subtle font-black text-primary uppercase group-hover:text-primary-hover transition-colors">{asset.assetnum}</td>
              <td className="p-3.5 border-r border-line-subtle text-ink font-bold">{asset.description}</td>
              <td className="p-3.5 border-r border-line-subtle text-center">
                <StatusBadge status={asset.status} />
              </td>
              <td className="p-3.5 border-r border-line-subtle font-black text-ink-secondary italic text-center">{asset.location_id || '---'}</td>
              <td className="p-3.5 border-r border-line-subtle font-black text-ink text-center uppercase">{asset.site_id || '---'}</td>
              <td className="p-3.5 border-r border-line-subtle text-ink-secondary font-black text-center">{asset.org_id || '---'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </ListPageShell>

      <AssetRecycleBin
        isOpen={showRecycleBin}
        onClose={() => setShowRecycleBin(false)}
        onRestored={fetchAssets}
      />

      {isBulkMoveOpen && (
        <BulkMoveModal 
          isOpen={isBulkMoveOpen} 
          onClose={() => {setIsBulkMoveOpen(false); setSelectedAssetIds([]);}} 
          selectedIds={selectedAssetIds}
          onRefresh={fetchAssets}
          onOpenLookup={handleSmartSearch}
          t={t}
          isRTL={isRTL}
          sessionOrg={sessionOrg}
          sessionSite={sessionSite}
        />
      )}
      {isBulkStatusOpen && (
        <BulkStatusModal 
          isOpen={isBulkStatusOpen} 
          onClose={() => {setIsBulkStatusOpen(false); setSelectedAssetIds([]);}} 
          selectedIds={selectedAssetIds}
          onRefresh={fetchAssets}
          t={t}
          isRTL={isRTL}
        />
      )}
      <UniversalLookup 
        {...lookup} 
        onClose={() => setLookup({...lookup, isOpen: false})} 
        onSelect={(row) => {
          if (lookup.onCustomSelect) {
            lookup.onCustomSelect(row);
          } else {
            const val = row.assetnum || row.location_id || row.itemnum || row.meter_num || row.value || row.id || row.site_id;
            handleInputChange(lookup.field, val);
          }
          setLookup(prev => ({ ...prev, isOpen: false }));
        }} 
      />
    </div>
  );
};

const BulkMoveModal = ({ isOpen, onClose, selectedIds, onRefresh, onOpenLookup, t, isRTL }) => {
  const [targetLoc, setTargetLoc] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);

  const handleBulkSubmit = async () => {
    setIsProcessing(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post('/api/assets/bulk-move', {
        asset_ids: selectedIds,
        new_location: targetLoc
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(t('reports.toasts.save_success'));
      onRefresh();
      onClose();
    } catch (err) {
      toast.error(t('reports.toasts.save_fail'));
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-md p-4" dir={isRTL ? 'rtl' : 'ltr'}>
      <div className={`bg-white w-full max-w-[550px] shadow-2xl border-t-8 border-[#1a3353] rounded-sm overflow-hidden animate-in zoom-in-95 duration-200 ${isRTL ? 'font-cairo' : ''}`}>
        <div className={`p-5 border-b flex justify-between items-center bg-slate-50 `}>
          <h2 className="font-black text-[#1a3353] uppercase tracking-widest text-sm flex items-center gap-3">
            <MapPin size={22} className="text-orange-500" /> {t('assets.modal.migration_title')}
          </h2>
          <X size={20} className="cursor-pointer text-slate-400 hover:text-red-500 transition-colors" onClick={onClose} />
        </div>
        
        <div className="p-8 space-y-6">
          <div className="bg-orange-50 border-l-4 border-orange-400 p-4">
            <p className={`text-[11px] font-black text-orange-800 uppercase leading-relaxed ${isRTL ? 'text-right' : ''}`}>
              {t('assets.modal.caution_prefix')} <span className="underline">{selectedIds.length} {t('assets.modal.caution_suffix')}</span>
            </p>
          </div>

          <div className="space-y-2 text-left">
            <label className={`text-[10px] font-black text-slate-400 uppercase tracking-widest ${isRTL ? 'text-right block' : ''}`}>{t('assets.modal.dest_label')}</label>
            <div className={`relative flex items-center group ${isRTL ? 'flex-row-reverse' : ''}`}>
              <input 
                className="w-full border-b-2 border-slate-200 p-3 pr-10 text-lg font-black text-blue-900 outline-none focus:border-blue-600 transition-all uppercase placeholder:text-slate-300"
                placeholder={t('assets.modal.placeholder')}
                value={targetLoc}
                onChange={(e) => setTargetLoc(e.target.value.toUpperCase())}
              />
              <div 
                className={`absolute ${isRTL ? 'left-0' : 'right-0'} bottom-2 p-2 cursor-pointer hover:scale-110 transition-transform text-blue-600`}
                onClick={() => onOpenLookup('targetLoc', t('reports.designer.fields.location'), 'locations', (row) => {
                    setTargetLoc(row.location_id || row.location);
                })}
              >
                <Search size={22} strokeWidth={3} />
              </div>
            </div>
          </div>
        </div>

        <div className={`p-4 bg-slate-50 border-t flex justify-end gap-3 px-8`}>
          <button onClick={onClose} className="px-6 py-2 font-black uppercase text-slate-400 text-[11px] hover:text-slate-600 transition-colors">{t('assets.modal.abort')}</button>
          <button 
            disabled={!targetLoc || isProcessing}
            onClick={handleBulkSubmit}
            className="bg-[#005495] text-white px-10 py-2.5 rounded-sm font-black uppercase text-[11px] shadow-xl hover:bg-black disabled:opacity-30 transition-all flex items-center gap-2"
          >
            {isProcessing ? <RefreshCw className="animate-spin" size={14}/> : t('assets.modal.execute')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AssetsList;